import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Timer, History, ListOrdered } from "lucide-react";
import { toast } from "react-toastify";
import { ConfirmationModal } from "../components/ui/ConfirmationModal";
import { CreateSessionDrawer } from "../components/sunbeds/CreateSessionDrawer";
import { SunbedSessionLogs } from "../components/sunbeds/SunbedSessionLogs";
import { cn } from "../lib/utils";
import { Button } from "../components/ui/Button";
import { SunbedActionToolbar } from "../components/sunbeds/SunbedActionToolbar";
import { PaginationFooter } from "../components/sunbeds/PaginationFooter";
import {
  SunbedSessionsTable,
} from "../components/sunbeds/SunbedSessionsTable";
import {
  createSunbedSession,
  getSunbedSessions,
  finishSunbedSession,
  cancelSunbedSession,
  SunbedSessionApiItem,
} from "../services/sunbeds";
import { checkTmaxHealth, startTmaxSession, cancelTmaxSession } from "../services/tmax";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../hooks/useToast";
import { useSunbedSessions } from "../context/SunbedSessionsContext";
import { hasSunbedWaitingListEnabled } from "../lib/sunbedWaitingList";
import {
  addSunbedWaitingListEntry,
  getSunbedWaitingList,
  getSunbedWaitingListErrorMessage,
} from "../services/sunbedWaitingList";

const WAITING_LIST_COUNT_POLL_MS = 60_000;

export type SunbedSessionPageProps = {
  /** Dedicated kiosk URL (`/sunbed-sessions-kiosk`) — title copy + 1-minute session polling. */
  kiosk?: boolean;
};

const SunbedSessionPage: React.FC<SunbedSessionPageProps> = ({ kiosk = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const waitingListEnabled = hasSunbedWaitingListEnabled(user);
  const { activeSessions, refreshSessions } = useSunbedSessions();
  const [sessions, setSessions] = useState<SunbedSessionApiItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [addingToWaitingList, setAddingToWaitingList] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [waitingListCount, setWaitingListCount] = useState(0);

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    onConfirm: () => void;
    title: string;
    message: string;
    variant: "danger" | "warning";
  }>({
    isOpen: false,
    onConfirm: () => { },
    title: "",
    message: "",
    variant: "danger",
  });

  const fetchSessions = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const response = await getSunbedSessions(searchTerm);
      if (response && response.data) {
        setSessions(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch sunbed sessions", error);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [searchTerm]);

  // Sync with global context
  useEffect(() => {
    setSessions(activeSessions);
  }, [activeSessions]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  /** Keep sessions list fresh on both web + kiosk (status/remaining stay aligned). */
  useEffect(() => {
    const intervalMs = 5_000;
    const id = window.setInterval(() => {
      void (async () => {
        try {
          await refreshSessions();
          await fetchSessions({ silent: true });
        } catch (e) {
          console.error("Sunbed session poll failed", e);
        }
      })();
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [refreshSessions, fetchSessions]);

  const loadWaitingListCount = useCallback(async () => {
    if (!waitingListEnabled || kiosk) return;
    try {
      const rows = await getSunbedWaitingList();
      setWaitingListCount(
        rows.filter((r) => String(r.status).toLowerCase() === "waiting").length
      );
    } catch {
      setWaitingListCount(0);
    }
  }, [waitingListEnabled, kiosk]);

  useEffect(() => {
    if (!waitingListEnabled || kiosk) return;
    void loadWaitingListCount();
    const id = window.setInterval(() => {
      void loadWaitingListCount();
    }, WAITING_LIST_COUNT_POLL_MS);
    return () => window.clearInterval(id);
  }, [waitingListEnabled, kiosk, loadWaitingListCount]);

  const filteredSessions = sessions.filter((s) => {
    const term = searchTerm.toLowerCase();
    return (
      String(s.customer ?? "").toLowerCase().includes(term) ||
      String(s.sunbed_room ?? "").toLowerCase().includes(term)
    );
  });
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedSessions = filteredSessions.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showLogs]);

  const handleAddToWaitingList = async (data: {
    customerId: number;
    packageId?: number;
    sunbedPackageId?: number;
    membershipId?: number;
    duration: string;
    delay: string;
    preferredRoomId?: string;
  }) => {
    setAddingToWaitingList(true);
    try {
      await addSunbedWaitingListEntry({
        user_id: data.customerId,
        package_id: data.packageId,
        sunbed_package_id: data.packageId ?? data.sunbedPackageId,
        membership_id: data.membershipId,
        duration_minutes: parseInt(data.duration, 10) || 1,
        delay_minutes: parseInt(data.delay, 10) || 0,
        preferred_room_id: data.preferredRoomId
          ? parseInt(data.preferredRoomId, 10)
          : null,
      });
      showToast.success("Customer added to waiting list.");
      setIsDrawerOpen(false);
      navigate("/sunbed-session/waiting-list");
    } catch (error) {
      console.error("Failed to add to waiting list", error);
      showToast.error(getSunbedWaitingListErrorMessage(error));
    } finally {
      setAddingToWaitingList(false);
    }
  };

  const handleCreateSession = async (data: any) => {
    try {
      const isTmaxEnabled = user?.company?.is_sunbed_tmax;

      if (isTmaxEnabled) {
        // Step 1: Health Check
        try {
          const health = await checkTmaxHealth();
          if (!health || !health.ok) { // Assuming health returns { ok: true }
            // Fallback or alert if health strictness is needed
            // For now, proceed if no error thrown, or check specific property if known
          }
        } catch (healthError) {
          console.error("TMAX Health Check Failed", healthError);
          showToast.error("The third-party service is not available at the moment.");
          return;
        }

        // Step 2: Start TMAX Session
        if (!data.tmaxId) {
          showToast.warning("TMAX ID is missing for the selected room.");
          return;
        }

        try {
          await startTmaxSession(
            data.tmaxId,
            data.duration,
            parseInt(data.delay, 10) || 0,
          );
        } catch (startError) {
          console.error("Failed to start TMAX session", startError);
          showToast.error("Failed to start the session with the third-party service.");
          return;
        }
      }

      // Step 3: Create Backend Session (Always runs if TMAX succeeded or TMAX disabled)
      const userId = data.customerId || 1;

      const payload: Record<string, unknown> = {
        user_id: userId,
        room_id: parseInt(data.room) || 1,
        interval: parseInt(data.duration) || 10,
        delay: parseInt(data.delay) || 0,
        remaining_balance: data.remainingBalance ?? 0,
      };

      if (data.membershipId) {
        payload.membership_id = data.membershipId;
      } else {
        payload.package_id = data.packageId;
      }

      // Since room_id is required and data.room might be a label, let's try to preserve what was there.
      // But if we have tmaxId (which came from room object), maybe we can use that if backend supports mapping?
      // Reverting to original payload logic for safety, unless I see obvious issue.

      const response = await createSunbedSession(payload as any);
      if (response.success || response.message || response.status === "success") {
        showToast.success("Session created successfully.");
        await refreshSessions();
        fetchSessions();
        setIsDrawerOpen(false);
      }
    } catch (error: any) {
      console.error("Failed to create session", error);
      const message =
        error?.message ||
        error?.response?.data?.message ||
        (error?.type === "server" || error?.type === "network"
          ? error.message
          : null) ||
        "Failed to create session";
      showToast.error(message);
    }
  };

  const finishingIdsRef = React.useRef<Set<string>>(new Set());

  const handleFinishSession = async (id: string | number) => {
    const key = String(id);
    if (finishingIdsRef.current.has(key)) return;
    finishingIdsRef.current.add(key);
    try {
      await finishSunbedSession(id);
      await refreshSessions();
      await fetchSessions({ silent: true });
      // Soft success — avoid noisy toasts when auto-finish races with context/poll
    } catch (error: any) {
      const message = String(
        error?.message || error?.response?.data?.message || "Failed to finish session"
      );
      // Already finished by cron/context/poll — ignore
      if (!/already|not running|completed/i.test(message)) {
        console.error("Failed to finish session", error);
        showToast.error(message);
      } else {
        await refreshSessions();
        await fetchSessions({ silent: true });
      }
    } finally {
      finishingIdsRef.current.delete(key);
    }
  };

  const handleCancelSession = async (id: string | number) => {
    setConfirmModal({
      isOpen: true,
      title: "Cancel Session",
      message: "Are you sure you want to cancel this session? This action cannot be undone.",
      variant: "danger",
      onConfirm: async () => {
        try {
          const isTmaxEnabled = user?.company?.is_sunbed_tmax;
          if (isTmaxEnabled) {
            const session = sessions.find((s) => s.id === id);
            if (session && session.tmax_id) {
              try {
                await cancelTmaxSession(session.tmax_id);
              } catch (tmaxError) {
                console.error("Failed to cancel TMAX session", tmaxError);
                showToast.error("Failed to cancel session with the third-party service.");
                return;
              }
            }
          }

          await cancelSunbedSession(id);
          showToast.success("Session cancelled successfully.");
          await refreshSessions(); // Refresh global context
          fetchSessions();
        } catch (error) {
          console.error("Failed to cancel session", error);
          showToast.error("Failed to cancel session. Please try again.");
        }
      },
    });
  };

  return (
    <div>
      <div className="px-6">
        <div className="flex items-center justify-between px-6 py-6 bg-white border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="bg-[#F3E8FF] p-3 rounded-xl">
              {showLogs ? (
                <History className="h-6 w-6 text-[#885ABB]" />
              ) : (
                <Timer className="h-6 w-6 text-[#885ABB]" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                {showLogs
                  ? "Session Logs"
                  : kiosk
                    ? "Sunbed Sessions (Kiosk)"
                    : "Sunbed Sessions"}
              </h1>
              <p className="text-sm text-gray-500 font-medium">
                {showLogs
                  ? "View past session history details"
                  : kiosk
                    ? "Kiosk view — sessions refresh every minute; monitor and manage active sunbed sessions"
                    : "Monitor and manage active sunbed sessions"}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowLogs(!showLogs)}
              className={cn(
                "gap-2 border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900",
                showLogs && "bg-gray-100 text-gray-900"
              )}
            >
              {showLogs ? <Timer size={16} /> : <History size={16} />}
              {showLogs ? "Active Sessions" : "View Logs"}
            </Button>
            {!showLogs && waitingListEnabled && !kiosk && (
              <Button
                variant="outline"
                onClick={() => navigate("/sunbed-session/waiting-list")}
                className="relative gap-2 border-[#E9D5FF] text-[#885ABB] hover:bg-[#F3E8FF] hover:text-[#7748A4]"
              >
                <ListOrdered size={16} />
                Waiting List
                {waitingListCount > 0 && (
                  <span className="min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[#885ABB] text-white text-xs font-bold leading-5 text-center">
                    {waitingListCount > 99 ? "99+" : waitingListCount}
                  </span>
                )}
              </Button>
            )}
            {!showLogs && (
              <Button
                onClick={() => setIsDrawerOpen(true)}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white gap-2 shadow-sm"
              >
                <Plus size={18} />
                New Session
              </Button>
            )}
          </div>
        </div>
      </div>

      {showLogs ? (
        <div className="flex-1 overflow-hidden p-6">
          <SunbedSessionLogs />
        </div>
      ) : (
        <div className="flex-1 flex flex-col p-6 space-y-6 overflow-hidden">
          {/* Action Toolbar */}
          <SunbedActionToolbar
            totalItems={sessions.length}
            showingStart={
              paginatedSessions.length > 0 ? indexOfFirstItem + 1 : 0
            }
            showingEnd={Math.min(indexOfLastItem, filteredSessions.length)}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search sessions..."
          />

          {/* Table */}
          <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <SunbedSessionsTable
              sessions={paginatedSessions}
              loading={loading}
              indexOfFirstItem={indexOfFirstItem}
              onFinish={(id) => handleFinishSession(id)}
              onCancel={(id) => handleCancelSession(id)}
            />

            {/* Pagination Footer */}
            {!loading && (
              <PaginationFooter
                totalItems={sessions.length}
                filteredTotal={filteredSessions.length}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        </div>
      )}

      <CreateSessionDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={handleCreateSession}
        waitingListEnabled={waitingListEnabled && !kiosk}
        onAddToWaitingList={handleAddToWaitingList}
        addingToWaitingList={addingToWaitingList}
      />

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmLabel="Yes, Cancel Session"
      />
    </div>
  );
};

export default SunbedSessionPage;
